import { CheckCircleOutlineOutlined, CloseOutlined } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import UpdateGarageLocationStyles from "./UpdateGarageLocationStyles";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { IPolicyVehicle } from "../../../../../models/user/policyDetails/PolicyDetails";
import { GetEditGarageLocationPAFormFields } from "../../../../../store/user/vehicle/vehicleActions";
import { RootState } from "../../../../../store/store";
import { useTranslation } from "react-i18next";
import { IDynamicFormFieldsJson } from "../../../../../models/user/DynamicForm/DynamicForm";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  vehicle: IPolicyVehicle;
}

const UpdateGarageLocation: React.FC<IProps> = ({ open, setOpen, vehicle }) => {
  const dispatch = useAppDispatch();
  const { editGarageLocationPAFormFields } = useAppSelector(
    (state: RootState) => state.vehicle
  );
  const [formFieldsJson, setFormFieldsJson] =
    useState<IDynamicFormFieldsJson | null>(null);

  // const [formValues, setFormValues] = useState<{ [key: string]: any }>({});
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      dispatch(GetEditGarageLocationPAFormFields());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (editGarageLocationPAFormFields) {
      setFormFieldsJson(editGarageLocationPAFormFields);
    }
  }, [editGarageLocationPAFormFields]);
  useEffect(() => {
    if (editGarageLocationPAFormFields && vehicle) {
      const updatedFormFieldsJson = {
        ...editGarageLocationPAFormFields,
        sections:
          editGarageLocationPAFormFields.sections?.map((section) => ({
            ...section,
            fields: section?.fields.map((field) => {
              const updatedField = { ...field };

              if (updatedField.name === "address") {
                updatedField.value = vehicle?.address?.addr1;
              } else if (updatedField.name === "city") {
                updatedField.value = vehicle?.address?.city;
              } else if (updatedField.name === "state") {
                updatedField.value = vehicle?.address?.stateProvCd;
              } else if (updatedField.name === "zip") {
                updatedField.value = vehicle?.address?.postalCode;
              }
              return updatedField;
            }),
          })) || [],
      };
      setFormFieldsJson(updatedFormFieldsJson);
    }
  }, [editGarageLocationPAFormFields, vehicle]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    handleClose();
  }, [setOpen]);

  const handleFormSubmit = () => {
    setMessageDialogOpen(true);
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "policyChange/updateGarageLocation"
    ) {
      setOpen(false);
      setMessageDialogOpen(true);
    }
  }, [isSuccess, isError, calledReducerType, setOpen, dispatch]);

  return (
    <>
      <DialogContent
        id="update_garage_location_content"
        data-test-id="update_garage_location_content"
        aria-label="Update garage location form"
      >
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleFormSubmit}
          />
        ) : (
          <></>
        )}
      </DialogContent>
      <Dialog 
        open={messageDialogOpen} 
        sx={{ backdropFilter: "blur(5px)" }}
        id="update_garage_location_message_dialog"
        data-test-id="update_garage_location_message_dialog"
        aria-label="Update garage location message dialog"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          id="update_garage_location_message_title"
          data-test-id="update_garage_location_message_title"
          aria-label="Update garage location message title"
        >
          <IconButton 
            onClick={handleMessageDialogClose}
            id="update_garage_location_close_button"
            data-test-id="update_garage_location_close_button"
            aria-label="Close message dialog"
          >
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <DialogContent
          id="update_garage_location_message_content"
          data-test-id="update_garage_location_message_content"
          aria-label="Update garage location message content"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexDirection: "column",
              justifyContent: "center",
              gap: 1,
              width: "100%",
            }}
            id="update_garage_location_message_box"
            data-test-id="update_garage_location_message_box"
            aria-label="Update garage location message container"
          >
            <CheckCircleOutlineOutlined
              sx={{ fontSize: "60px", color: "green" }}
              id="update_garage_location_success_icon"
              data-test-id="update_garage_location_success_icon"
              aria-label="Success icon"
            />
            <Typography
              id="update_garage_location_success_message"
              data-test-id="update_garage_location_success_message"
              aria-label="Policy change initiated message"
            >
              {t("Policy Change has been initiated")}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UpdateGarageLocation;
